import { RuleModel } from 'src/types/rule.model';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import { TransactionModel } from 'src/types/transaction.model';
import LiteralsService from 'src/services/literals/LiteralsService';
import * as Yup from 'yup';
import { SchemaOf } from 'yup';

export function getMatcherText(matcher: RuleMatcher): string {
    switch (matcher) {
        case RuleMatcher.CONTAINS:
            return LiteralsService.getLiteral('Rule.containsText');
        case RuleMatcher.EQUALS:
            return LiteralsService.getLiteral('Rule.equalsText');
        case RuleMatcher.STARTS_WITH:
            return LiteralsService.getLiteral('Rule.startsWithText');
        case RuleMatcher.ENDS_WITH:
            return LiteralsService.getLiteral('Rule.endsWithText');
    }
}

export function getRuleName(rule: Partial<RuleModel>, categoryName?: string): string {
    return LiteralsService.getLiteral('Rule.name', [
        rule?.matcher ? getMatcherText(rule.matcher) : 'blank',
        rule?.transactionName ? rule.transactionName : 'blank',
        categoryName || 'blank'
    ]);
}

export function matchesRule(transaction: TransactionModel, rule: Partial<RuleModel>): Boolean {
    const ruleMatcher: string = rule.transactionName.toLowerCase();
    const endsWith = (name: string): boolean => name.toLowerCase().endsWith(ruleMatcher);
    const contains = (name: string): boolean => name.toLowerCase().includes(ruleMatcher);
    const startsWith = (name: string): boolean => name.toLowerCase().startsWith(ruleMatcher);
    const equals = (name: string): boolean => name.toLowerCase() === ruleMatcher;

    switch (rule.matcher) {
        case RuleMatcher.ENDS_WITH:
            return endsWith(transaction.displayName) || endsWith(transaction.merchantName);

        case RuleMatcher.CONTAINS:
            return contains(transaction.displayName) || contains(transaction.merchantName);

        case RuleMatcher.STARTS_WITH:
            return startsWith(transaction.displayName) || startsWith(transaction.merchantName);

        case RuleMatcher.EQUALS:
            return equals(transaction.displayName) || equals(transaction.merchantName);
    }
}

/** Retrieves the priorities from a list of rules */
export function getPriorities(rules: RuleModel[]): number[] {
    return rules.map((rule) => rule.priority);
}

export function matchingRules(rule1: Partial<RuleModel>, rule2: Partial<RuleModel>): boolean {
    return (
        rule1.transactionName.toLowerCase() === rule2.transactionName.toLowerCase() && rule1.matcher === rule2.matcher
    );
}

/** Retrieves the rule with the lowest priority that matches the transaction. Priority 0 is the highest priority */
export function getLowerPriorityMatchingRule(transaction: TransactionModel, rules: RuleModel[]): RuleModel {
    const sortedRules = rules?.sort((a, b) => b.priority - a.priority);
    return sortedRules?.find((rule) => matchesRule(transaction, rule));
}

export function getRuleInitialValues(rule: Partial<RuleModel>): {
    ruleMatcher: RuleMatcher;
    ruleTransactionName: string;
    ruleGroup: string;
} {
    return {
        ruleMatcher: rule.matcher,
        ruleTransactionName: rule.transactionName,
        ruleGroup: rule.groupUid
    };
}

export function getRuleValidationSchema(): SchemaOf<{
    ruleMatcher: string;
    ruleTransactionName: string;
    ruleGroup: string;
}> {
    return Yup.object({
        ruleMatcher: Yup.string().required(LiteralsService.getLiteral('FormErrors.required')),
        ruleTransactionName: Yup.string().required(LiteralsService.getLiteral('FormErrors.nameInputError')),
        ruleGroup: Yup.string()
            .required(LiteralsService.getLiteral('FormErrors.categoryRequired'))
            .min(1, LiteralsService.getLiteral('FormErrors.categoryRequired'))
    });
}
